import {
    FUEL_PRICE_STATUS,
    FUEL_PRICE_STATUS_COLORS,
    FUEL_PRICE_STATUS_LABELS,
    PRICE_SOURCE_LABELS,
    PRICE_SNAPSHOT_ROLE,
    PRICE_SNAPSHOT_ROLE_COLORS,
} from "../constants";
import {
    EMPTY_VALUE,
    formatCurrencyUnit,
    formatDateTime,
    formatPriceDisplay,
    formatSignedNumber,
    formatSignedPercent,
    getDifferenceColor,
    isNumber,
} from "../utils/formatters";

export function unwrapApiResponse(response) {
    return response?.data ?? response;
}

export function createDefaultWorkbenchFilters() {
    return {
        vendor_id: undefined,
        airport_code: undefined,
        status: undefined,
        dateRange: null,
        search_term: undefined,
    };
}

export function buildPricePeriodQueryParams(filters, page, pageSize) {
    const params = { page, page_size: pageSize };

    if (filters.vendor_id) params.vendor_id = filters.vendor_id;
    if (filters.airport_code) params.airport_code = filters.airport_code;
    if (filters.status) params.status = filters.status;
    if (filters.search_term) params.search_term = filters.search_term;

    if (filters.dateRange && filters.dateRange.length === 2) {
        params.period_start_from = filters.dateRange[0].format("YYYY-MM-DD");
        params.period_start_to = filters.dateRange[1].format("YYYY-MM-DD");
    }

    return params;
}

export function getFuelPriceStatusOptions() {
    return Object.entries(FUEL_PRICE_STATUS_LABELS).map(([value, label]) => ({ value, label }));
}

export function buildFuelPriceStats(periods, total) {
    return {
        total,
        draft: periods.filter((period) => period.status === FUEL_PRICE_STATUS.DRAFT).length,
        pending: periods.filter((period) => period.status === FUEL_PRICE_STATUS.PENDING_CONFIRMATION).length,
        confirmed: periods.filter((period) => period.status === FUEL_PRICE_STATUS.CONFIRMED).length,
        disputed: periods.filter((period) => period.status === FUEL_PRICE_STATUS.DISPUTED).length,
    };
}

export function getPricePeriodActionState(period) {
    return {
        canConfirm:
            period?.status === FUEL_PRICE_STATUS.DRAFT ||
            period?.status === FUEL_PRICE_STATUS.PENDING_CONFIRMATION,
        canDispute:
            period?.status === FUEL_PRICE_STATUS.PENDING_CONFIRMATION ||
            period?.status === FUEL_PRICE_STATUS.CONFIRMED,
        canDelete: period?.status === FUEL_PRICE_STATUS.DRAFT,
    };
}

export function getPriceInputModalConfig(snapshotRole) {
    if (snapshotRole === PRICE_SNAPSHOT_ROLE.SUPPLIER_QUOTE) {
        return {
            title: "录入供应商报价快照",
            label: "供应商确认单价",
        };
    }

    return {
        title: "更新我方参考快照",
        label: "我方预算单价",
    };
}

export function mapPricePeriodToWorkbenchRecord(period) {
    const currency = period?.price_currency;
    const unit = period?.price_unit;

    return {
        ...period,
        vendorDisplayName: period?.vendor?.name || period?.vendor_id?.substring(0, 8) || EMPTY_VALUE,
        periodRangeLabel:
            period?.period_start && period?.period_end
                ? `${period.period_start} ~ ${period.period_end}`
                : EMPTY_VALUE,
        unitDisplay: formatCurrencyUnit(currency, unit),
        statusLabel: FUEL_PRICE_STATUS_LABELS[period?.status] || period?.status || EMPTY_VALUE,
        statusColor: FUEL_PRICE_STATUS_COLORS[period?.status] || "default",
        ourPriceDisplay: formatPriceDisplay(period?.our_calculated_price),
        ourPriceFullDisplay: formatPriceDisplay(period?.our_calculated_price, {
            currency,
            unit,
            withUnit: true,
        }),
        supplierPriceDisplay: formatPriceDisplay(period?.supplier_quoted_price),
        supplierPriceFullDisplay: formatPriceDisplay(period?.supplier_quoted_price, {
            currency,
            unit,
            withUnit: true,
        }),
        finalConfirmedDisplay: formatPriceDisplay(period?.final_confirmed_price, {
            currency,
            unit,
            withUnit: true,
        }),
        differenceValueDisplay: formatSignedNumber(period?.price_difference),
        differencePercentDisplay: formatSignedPercent(period?.price_difference_percent),
        differenceColor: getDifferenceColor(period?.price_difference),
        hasBothPrices:
            isNumber(period?.our_calculated_price) && isNumber(period?.supplier_quoted_price),
        actionState: getPricePeriodActionState(period),
    };
}

const CURRENT_BREAKDOWN_ROW_BUILDERS = [
    {
        key: "TOTAL_PER_UNIT",
        label: "统一口径总价",
        description: "当前 MVP 已落地的总价级比较锚点",
        buildInternal: (record) => record.ourPriceFullDisplay,
        buildSupplier: (record) => record.supplierPriceFullDisplay,
        buildDifference: (record) => {
            if (!record.hasBothPrices) {
                return "等待双侧价格齐备后再进行差异解释";
            }

            return `${record.differenceValueDisplay} / ${record.differencePercentDisplay}`;
        },
    },
    {
        key: "PRICE_SOURCE",
        label: "供应商来源",
        description: "当前仅供应商侧带来源语义",
        buildInternal: () => "系统内参考快照",
        buildSupplier: (record) =>
            PRICE_SOURCE_LABELS[record.price_source] || record.price_source || EMPTY_VALUE,
        buildDifference: (record) =>
            record.price_source ? "报价来源已记录" : "当前尚未记录供应商来源",
    },
    {
        key: "PERIOD_REMARKS",
        label: "周期备注",
        description: "当前备注仍挂在周期主记录上",
        buildInternal: (record) => record.remarks || EMPTY_VALUE,
        buildSupplier: (record) => record.remarks || EMPTY_VALUE,
        buildDifference: () => "备注仍是周期级共享上下文",
    },
    {
        key: "CONFIRMED_RESULT",
        label: "周期确认结果",
        description: "确认动作始终面向整个价格周期",
        buildInternal: (record) => record.finalConfirmedDisplay,
        buildSupplier: (record) => record.finalConfirmedDisplay,
        buildDifference: (record) =>
            record.confirmed_at
                ? `已于 ${formatDateTime(record.confirmed_at, "YYYY-MM-DD HH:mm")} 完成确认`
                : "当前尚未形成最终确认价",
    },
];

const PLANNED_BREAKDOWN_ROWS = [
    {
        key: "BASE_FOB",
        label: "Base FOB",
        description: "待后端快照化后接入 internal vs supplier 分项对比",
    },
    {
        key: "DIFFERENTIAL",
        label: "Differential",
        description: "待按合同规则层与周期快照层拆开后接入",
    },
    {
        key: "SAF",
        label: "SAF",
        description: "待供应商报价快照支持结构化附加项后接入",
    },
    {
        key: "INFRA_FEE_LOCAL",
        label: "Infrastructure Fee (Local)",
        description: "待接入原币原单位的 Infrastructure Fee 快照",
    },
    {
        key: "INFRA_FEE_NORMALIZED",
        label: "Infrastructure Fee (Normalized)",
        description: "待接入折算后统一口径的 Infrastructure Fee 快照",
    },
    {
        key: "FX_RATE",
        label: "FX Rate",
        description: "待参考数据层与周期快照层建立追溯链路后接入",
    },
    {
        key: "TOTAL_PER_UNIT",
        label: "Total Per Unit",
        description: "待周期快照支持统一口径总价 breakdown 后接入",
    },
    {
        key: "TOTAL_PER_TON",
        label: "Total Per Ton",
        description: "待周期快照支持另一结算口径后接入",
    },
];

export function buildPriceComparisonViewModel(period) {
    if (!period) {
        return null;
    }

    const record = mapPricePeriodToWorkbenchRecord(period);

    return {
        title: `${record.vendorDisplayName} · ${record.airport_code || EMPTY_VALUE}`,
        meta: [
            { label: "价格周期", value: record.periodRangeLabel },
            { label: "周期状态", value: record.statusLabel },
            { label: "机场", value: record.airport_code || EMPTY_VALUE },
            { label: "币种 / 单位", value: record.unitDisplay },
        ],
        actionSummary: [
            { label: "主对象", value: "一个周期主记录" },
            { label: "比较对象", value: "我方参考快照 vs 供应商报价快照" },
            { label: "动作语义", value: "确认、争议、删除都面向整个价格周期" },
        ],
        snapshots: [
            {
                key: PRICE_SNAPSHOT_ROLE.INTERNAL_REFERENCE,
                title: "INTERNAL_REFERENCE",
                subtitle: "我方参考快照",
                color: PRICE_SNAPSHOT_ROLE_COLORS[PRICE_SNAPSHOT_ROLE.INTERNAL_REFERENCE],
                total: record.ourPriceFullDisplay,
                rows: [
                    { label: "统一口径总价", value: record.ourPriceFullDisplay },
                    { label: "创建时间", value: formatDateTime(record.created_at, "YYYY-MM-DD HH:mm") },
                    { label: "快照角色", value: "内部参考，用于周期级判断" },
                ],
            },
            {
                key: PRICE_SNAPSHOT_ROLE.SUPPLIER_QUOTE,
                title: "SUPPLIER_QUOTE",
                subtitle: "供应商报价快照",
                color: PRICE_SNAPSHOT_ROLE_COLORS[PRICE_SNAPSHOT_ROLE.SUPPLIER_QUOTE],
                total: record.supplierPriceFullDisplay,
                rows: [
                    { label: "统一口径总价", value: record.supplierPriceFullDisplay },
                    {
                        label: "价格来源",
                        value:
                            PRICE_SOURCE_LABELS[record.price_source] || record.price_source || EMPTY_VALUE,
                    },
                    { label: "快照角色", value: "供应商报价，用于周期级确认" },
                ],
            },
        ],
        differenceBanner: record.hasBothPrices
            ? {
                  type: "warning",
                  message: `当前总价差异为 ${record.differenceValueDisplay}（${record.differencePercentDisplay}）。`,
              }
            : {
                  type: "info",
                  message: "当前只具备单侧价格，工作台已保留双快照骨架，但差异解释仍有限。",
              },
        breakdownRows: CURRENT_BREAKDOWN_ROW_BUILDERS.map((builder) => ({
            key: builder.key,
            label: builder.label,
            description: builder.description,
            internalValue: builder.buildInternal(record),
            supplierValue: builder.buildSupplier(record),
            differenceHint: builder.buildDifference(record),
            status: "current",
        })),
        plannedBreakdownRows: PLANNED_BREAKDOWN_ROWS.map((row) => ({
            ...row,
            internalValue: "待后端快照化",
            supplierValue: "待后端快照化",
            differenceHint: row.description,
            status: "planned",
        })),
        futureHint:
            "当前浮层已经以周期主记录 + 双快照比较为稳定容器；后续只需补充分项快照，而不需要重做主交互。",
        actionState: record.actionState,
    };
}
